import { createStyles, makeStyles } from '@epilot/journey-elements'
import { clsx } from '@epilot/journey-logic-commons'
import type { LayoutProps, UISchemaElement } from '@jsonforms/core'
import { isLayout } from '@jsonforms/core'
import { JsonFormsDispatch } from '@jsonforms/react'

import { BlockContent } from '../BlockContent'
import { BlockHeading } from '../BlockHeading'

type GroupOptions = {
  name: string
  title: string
  subtitle: string
  showPaper: boolean
  display: boolean
}

function GroupLayout({
  uischema,
  schema,
  enabled,
  path,
  renderers
}: LayoutProps) {
  const classes = useStyles()

  if (!isLayout(uischema)) {
    return null
  }

  const { name, title, subtitle, showPaper, display } =
    uischema.options as GroupOptions
  const blocks = uischema.elements

  const mappedBlocks = blocks.map((block, index) => {
    const options: UISchemaElement['options'] = {
      ...block.options,
      isNested: true
    }

    block.options = options

    return (
      <div
        className={clsx(
          options.halfWidth ? classes.halfWidth : classes.fullWidth
        )}
        key={`${name}${index}`}
      >
        <JsonFormsDispatch
          enabled={enabled}
          path={path}
          renderers={renderers}
          schema={schema}
          uischema={block}
        />
      </div>
    )
  })

  if (!display) {
    return null
  }

  const content = (
    <BlockHeading
      isGroup
      subtitle={subtitle}
      title={title}
      useNewDesign={false}
    >
      <div className={classes.blocksContainer}>{mappedBlocks}</div>
    </BlockHeading>
  )

  return (
    <BlockContent isGroup showCard={showPaper} useNewDesign={false}>
      {content}
    </BlockContent>
  )
}

const useStyles = makeStyles((theme) => {
  return createStyles({
    blocksContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(3),

      // Remove padding top of nested class
      '& > div > div': {
        paddingTop: 0
      },

      // Use 12px as spacing for old design and 20px as spacing for new design (consistent with spacing)
      '& + &': {
        marginTop: theme.spacing(3)
      }
    },
    fullWidth: {
      width: '100%',
      // Allow wrapping for group layout on smaller screens
      '@media (min-width: 400px)': {
        minWidth: '270px'
      }
    },
    halfWidth: {
      flexGrow: 1,
      width: '45%',
      minWidth: '270px'
    }
  })
})

export default GroupLayout
